import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BlogEditor } from "@/components/blog/BlogEditor";
import { PageTranslateButton, type PageTranslationResult } from "@/components/admin/pages/PageTranslateButton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  CaretLeft,
  CircleNotch,
  FloppyDisk,
  PaperPlaneTilt,
  Image as ImageIcon,
  Eye,
  EyeSlash,
  Globe,
  Translate,
} from "@phosphor-icons/react";
import { MediaPicker } from "@/components/ui/media-picker";
import { toast } from "@/lib/toast";
import { throwIfNotOk } from "@/lib/api-error";
import { countWords } from "@/lib/content-helpers";
import { LOCALES, LOCALE_MAP } from "@/lib/locale";

export const Route = createFileRoute("/dashboard/admin/pages/new")({
  component: AdminNewPage,
  validateSearch: (search: Record<string, unknown>) => ({
    groupId: (search.groupId as string) || undefined,
    locale: (search.locale as string) || undefined,
  }),
});


function AdminNewPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { groupId, locale: presetLocale } = Route.useSearch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [title, setTitle] = useState("");
  const [content, setContent] = useState<object | null>(null);
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [locale, setLocale] = useState(presetLocale || "bs");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [editorKey, setEditorKey] = useState(0);

  const isTranslation = !!groupId;

  const handleContentChange = useCallback((newContent: object) => {
    setContent(newContent);
  }, []);

  const handleTranslated = useCallback((translation: PageTranslationResult) => {
    setTitle(translation.title);
    setContent(translation.content);
    if (!coverImageUrl && translation.coverImageUrl) {
      setCoverImageUrl(translation.coverImageUrl);
    }
    setEditorKey((key) => key + 1);
  }, [coverImageUrl]);

  const canSubmit = title.trim().length > 0 && content !== null;

  const handleSubmit = async (status: "draft" | "published") => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const res = await fetch("/api/pages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          content: JSON.stringify(content),
          coverImageUrl: coverImageUrl || undefined,
          locale,
          groupId: groupId || undefined,
          status,
        }),
      });
      await throwIfNotOk(res, "Greška pri kreiranju stranice.");
      await queryClient.invalidateQueries({ queryKey: ["pages"] });
      navigate({ to: "/dashboard/admin/pages" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Greška pri kreiranju stranice.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const wordCount = content ? countWords(content) : 0;

  return (
    <div className="mx-auto max-w-7xl">
      {/* Top bar */}
      <div className="mb-6 flex items-center justify-between sm:mb-8">
        <Link
          to="/dashboard/admin/pages"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <CaretLeft className="size-4" />
          Stranice
        </Link>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden md:inline-flex"
            title={sidebarOpen ? "Sakrij panel" : "Prikaži panel"}
          >
            {sidebarOpen ? (
              <EyeSlash className="size-4" />
            ) : (
              <Eye className="size-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSubmit("draft")}
            disabled={isSubmitting || !canSubmit}
          >
            {isSubmitting ? (
              <CircleNotch className="size-3.5 animate-spin" />
            ) : (
              <FloppyDisk className="size-3.5" />
            )}
            Nacrt
          </Button>
          <Button
            size="sm"
            onClick={() => handleSubmit("published")}
            disabled={isSubmitting || !canSubmit}
          >
            {isSubmitting ? (
              <CircleNotch className="size-3.5 animate-spin" />
            ) : (
              <PaperPlaneTilt className="size-3.5" />
            )}
            Objavi
          </Button>
        </div>
      </div>

      {isTranslation && (
        <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50/50 p-3.5">
          <p className="text-sm text-blue-700">
            Kreirate prijevod postojeće stranice na{" "}
            <strong>{LOCALES.find((l) => l.value === locale)?.label}</strong> jeziku.
          </p>
        </div>
      )}

      <div className="flex gap-8">
        {/* Main editor area */}
        <div className="min-w-0 flex-1">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Naslov stranice..."
            className="w-full border-none bg-transparent text-3xl font-bold tracking-tight text-foreground outline-none placeholder:text-muted-foreground/30 sm:text-4xl"
            style={{ fontFamily: "var(--font-heading)" }}
          />

          <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
            <span>{wordCount} riječi</span>
            <span>~{Math.max(1, Math.ceil(wordCount / 200))} min čitanja</span>
            {!canSubmit && (
              <span className="text-amber-500">
                {!title.trim()
                  ? "Unesite naslov"
                  : "Počnite pisati sadržaj"}
              </span>
            )}
          </div>

          <div className="my-6 h-px bg-border" />

          <div className="[&_.rounded-lg]:rounded-none [&_.rounded-lg]:border-x-0 [&_.rounded-lg]:border-t-0 [&_.ProseMirror]:min-h-[500px] [&_.ProseMirror]:px-0">
            <BlogEditor
              key={editorKey}
              initialContent={content || undefined}
              onChange={handleContentChange}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div
          className={cn(
            "hidden shrink-0 transition-all duration-300 md:block",
            sidebarOpen ? "w-72 opacity-100" : "w-0 overflow-hidden opacity-0"
          )}
        >
          <div className="sticky top-6 space-y-5">
            {/* Language selector */}
            <div className="space-y-2">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Globe className="size-3.5" />
                Jezik
              </p>
              <div className="flex gap-1.5">
                {LOCALES.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => !isTranslation && setLocale(opt.value)}
                    disabled={isTranslation}
                    className={cn(
                      "flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all",
                      locale === opt.value
                        ? "bg-foreground text-background shadow-sm"
                        : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground",
                      isTranslation && locale !== opt.value && "opacity-40"
                    )}
                  >
                    {opt.flag} {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {locale === "en" && (
              <div className="space-y-2">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <Translate className="size-3.5" />
                  AI prijevod
                </p>
                <PageTranslateButton
                  title={title}
                  content={content}
                  groupId={groupId}
                  disabled={isSubmitting}
                  label={groupId ? "Prevedi iz BiH verzije" : "Prevedi trenutni tekst"}
                  onTranslated={handleTranslated}
                />
              </div>
            )}

            {/* Cover image */}
            <div className="space-y-2">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <ImageIcon className="size-3.5" />
                Naslovna slika
              </p>
              <MediaPicker
                value={coverImageUrl}
                onChange={setCoverImageUrl}
                folder="pages"
                hint="JPG, PNG ili WebP · maks. 5 MB"
                previewHeight="h-52 sm:h-64"
              />
            </div>

            <div className="rounded-xl border bg-muted/20 p-3.5">
              <p className="text-xs leading-relaxed text-muted-foreground">
                <strong className="text-foreground">Nacrt</strong> sprema
                stranicu bez objavljivanja.{" "}
                <strong className="text-foreground">Objavi</strong> je odmah
                stavlja na platformu.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sidebar */}
      <div className="mt-8 space-y-5 md:hidden">
        <div className="h-px bg-border" />

        {/* Mobile language selector */}
        <div className="space-y-2">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Globe className="size-3.5" />
            Jezik
          </p>
          <div className="flex gap-1.5">
            {LOCALES.map((opt) => (
              <Button
                key={opt.value}
                type="button"
                variant={locale === opt.value ? "default" : "secondary"}
                size="sm"
                onClick={() => !isTranslation && setLocale(opt.value)}
                disabled={isTranslation}
                className={cn(
                  "flex-1",
                  isTranslation && locale !== opt.value && "opacity-40"
                )}
              >
                {opt.flag} {opt.label}
              </Button>
            ))}
          </div>
        </div>

        {locale === "en" && (
          <div className="space-y-2">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Translate className="size-3.5" />
              AI prijevod
            </p>
            <PageTranslateButton
              title={title}
              content={content}
              groupId={groupId}
              disabled={isSubmitting}
              label={groupId ? "Prevedi iz BiH verzije" : "Prevedi trenutni tekst"}
              onTranslated={handleTranslated}
            />
          </div>
        )}

        <div className="space-y-2">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <ImageIcon className="size-3.5" />
            Naslovna slika
          </p>
          <MediaPicker
            value={coverImageUrl}
            onChange={setCoverImageUrl}
            folder="pages"
            hint="JPG, PNG ili WebP · maks. 5 MB"
            previewHeight="h-52 sm:h-64"
          />
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleSubmit("draft")}
            disabled={isSubmitting || !canSubmit}
            className="flex-1"
          >
            <FloppyDisk className="size-4" />
            Nacrt
          </Button>
          <Button
            onClick={() => handleSubmit("published")}
            disabled={isSubmitting || !canSubmit}
            className="flex-1"
          >
            <PaperPlaneTilt className="size-4" />
            Objavi
          </Button>
        </div>
      </div>
    </div>
  );
}
